import { Injectable } from '@angular/core';
import { catchError, from, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ProfileService } from '../../auth_profile/services/profile.service';
import { V3DeviceRegistration, V3EncryptionKeyValue } from '../model/v3/drm';
import { BookReaderApiV3Service } from './v3/book-reader-api-v3.service';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {

  private keyPair: CryptoKeyPair = null
  private publicKey: string
  private drmAccountId: number = null
  private deviceId: string
  private deviceKey: string

  private derivedKey: CryptoKey = null
  private encryptionKeyValue: V3EncryptionKeyValue

  constructor(
    private bookReaderApi: BookReaderApiV3Service,
    private profileService: ProfileService
  ) { }

  prepareEncryptionKeysIfNeeded(){
    return of(this.keyPair, this.publicKey).pipe(
      switchMap((keyPair, publicKey) => keyPair && publicKey ? of(null) : this.exportPublicKey()),
      switchMap(_ => this.drmAccountId ? of(null) : this.resolveDrmAccountId()),
      switchMap(_ => this.deviceKey && this.deviceId ? of(null) : this.setDeviceFromCookies()),
      switchMap(_ => this.encryptionKeyValue?.key ? of(null) : this.generateEncryptionKey()),
      switchMap(_ => this.derivedKey ? of(null) : this.importServerKey().pipe(
        switchMap(serverPublicKey => this.generateDerivedKey(serverPublicKey)),
        tap(key => this.derivedKey = key)
      )),
    )
  }

  refreshEncryptionKeys(){
    return this.exportPublicKey().pipe(
      switchMap(_ => this.generateEncryptionKey()),
      switchMap(_ => this.importServerKey()),
      switchMap(serverPublicKey => this.generateDerivedKey(serverPublicKey)),
      tap(key => this.derivedKey = key)
    )
  }

  generateKeys(): Observable<CryptoKeyPair> {
    return from(crypto.subtle.generateKey(
      {
        name: 'ECDH',
        namedCurve: 'P-256'
      },
      true,
      ['deriveKey']
    ))
  }

  exportPublicKey(): Observable<string> {
    return this.generateKeys().pipe(
      tap(keys => this.keyPair = keys),
      switchMap(_ => from(crypto.subtle.exportKey("spki", this.keyPair.publicKey))),
      map(exportedKey => this.publicKey = btoa(this.arrayBufferToString(exportedKey)))
    )
  }

  importServerKey(): Observable<CryptoKey> {
    return from(crypto.subtle.importKey(
      'spki',
      this.base64ToArrayBuffer(this.encryptionKeyValue.publicServerKey),
      {
        name: "ECDH",
        namedCurve: "P-256"
      },
      false,
      []
    ))
  }

  generateDerivedKey(serverPublicKey: CryptoKey): Observable<CryptoKey> {
    return from(crypto.subtle.deriveKey(
      {
        name: "ECDH",
        public: serverPublicKey,
      },
      this.keyPair.privateKey,
      {
        name: "AES-CBC",
        length: 128
      },
      false,
      ['decrypt']
    ))
  }

  decrypt(dataBase64: string): Observable<string> {
    const ivLength = 16
    const combinedMessageBytes = this.base64ToArrayBuffer(dataBase64);

    const iv = combinedMessageBytes.slice(0, ivLength);
    const encryptedMessage = combinedMessageBytes.slice(ivLength);

    return from(crypto.subtle.decrypt(
      {
        name: "AES-CBC",
        iv: iv
      },
      this.derivedKey,
      encryptedMessage
    )).pipe(
      map(decryptedData => new TextDecoder().decode(decryptedData))
    )
  }

  arrayBufferToString(buf: ArrayBuffer): string {
    return String.fromCharCode.apply(null, new Uint8Array(buf));
  }

  base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  generateEncryptionKey(retryOnInvalidDevice: boolean = true): Observable<V3EncryptionKeyValue> {
    return this.ensureDrmAccountId().pipe(
      switchMap(accountId => this.bookReaderApi.createProductKey(accountId, this.deviceId, this.deviceKey, this.publicKey)),
      tap(keyValue => this.encryptionKeyValue = keyValue),
      catchError(err => {
        if(retryOnInvalidDevice && err.status >= 400 && err.status < 500) {
          return this.registerDevice().pipe(
            switchMap(_ => this.generateEncryptionKey(false))
          )
        }
        else return of(null)
      })
    )
  }

  getEncryptionKey(): string {
    return this.encryptionKeyValue?.key
  }

  registerDevice(): Observable<V3DeviceRegistration> {
    return this.ensureDrmAccountId().pipe(
      switchMap(accountId => this.bookReaderApi.registerDeviceV2(accountId, {
        platform: environment.platform.name,
        fingerprintVector: {}
      })),
      tap(registration => {
        this.deviceId = registration.deviceId
        this.deviceKey = registration.deviceKey
        // drop stale entries first - the legacy flow (and builds before the path fix)
        // wrote cookies without an explicit path, and a path-scoped cookie would
        // shadow the root-scoped one on the pages it was written from
        this.clearCookie('deviceKey')
        this.clearCookie('deviceId')
        document.cookie = `deviceKey=${registration.deviceKey}; path=/`
        document.cookie = `deviceId=${registration.deviceId}; path=/`
      })
    )
  }

  /**
   * Expires the cookie on the current path and every ancestor path, so values
   * written historically without `path=/` cannot shadow the fresh registration.
   */
  private clearCookie(name: string) {
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
    document.cookie = `${name}=; ${expiry}`
    document.cookie = `${name}=; path=/; ${expiry}`
    let path = ''
    window.location.pathname.split('/').filter(segment => segment).forEach(segment => {
      path += '/' + segment
      document.cookie = `${name}=; path=${path}; ${expiry}`
    })
  }

  getDeviceKey(): string {
    return this.deviceKey
  }

  private ensureDrmAccountId(): Observable<number> {
    return this.drmAccountId ? of(this.drmAccountId) : this.resolveDrmAccountId()
  }

  private resolveDrmAccountId(): Observable<number> {
    return this.profileService.getSelfPerson().pipe(
      take(1),
      switchMap(person => person?.accountId != null
        ? this.bookReaderApi.getAccountForCasaAccount(person.accountId)
        : throwError(() => new Error('logged-in person has no CASA account id'))),
      switchMap(account => account
        ? of(account.id)
        : throwError(() => new Error('no DRM account linked to the CASA account'))),
      tap(accountId => this.drmAccountId = accountId)
    )
  }

  private setDeviceFromCookies(): Observable<any> {
    const deviceKey = this.getCookie('deviceKey')
    const deviceId = this.getCookie('deviceId')

    if(deviceKey && deviceId) {
      this.deviceKey = deviceKey
      this.deviceId = deviceId
      return of(null)
    }
    else return this.registerDevice()
  }

  getCookie(name: string) {
    return document.cookie
        .split('; ')
        .find(row => row.startsWith(name + '='))
        ?.split('=')[1] || null;
  }

}
